import { useEffect, useState } from 'react';
import { CheckCircleOutlined, CopyOutlined, MobileOutlined, PlusOutlined, SearchOutlined, ShopOutlined, StopOutlined } from '@ant-design/icons';
import { Alert, App, Button, Drawer, Form, Input, InputNumber, Modal, Segmented, Space, Switch, Tabs, Tag } from 'antd';
import { api } from '../api';
import { ActiveTag, DataTable, ErrorNotice, PageHeader, Panel, Person, RowActions } from '../components';
import { countLabel, formatDate, formatDateTime, MOBILE_RULES, mobileLabel, useLoad } from '../lib';
const money = (paise) => `₹${(Number(paise || 0) / 100).toFixed(2)}`;

function WalletDrawer({ account, onClose, onChanged }) {
    const { message } = App.useApp();
    const loaded = useLoad(() => account ? api(`/v1/admin/accounts/${account.id}/wallet`) : Promise.resolve(null), [account?.id]);
    const [amount, setAmount] = useState(0);
    const [reason, setReason] = useState('');
    const [detail, setDetail] = useState(null);
    async function adjust(direction) {
        try {
            await api(`/v1/admin/accounts/${account.id}/wallet/adjustments`, { method: 'POST', body: JSON.stringify({ direction, amountPaise: Math.round(amount * 100), reason }) });
            message.success(`Wallet ${direction} recorded`); setAmount(0); setReason(''); await loaded.reload(); await onChanged();
        } catch (error) { message.error(error instanceof Error ? error.message : 'Could not adjust wallet'); }
    }
    async function inspect(row) {
        try { setDetail((await api(`/v1/admin/accounts/${account.id}/api-calls/${row.id}`)).call); }
        catch (error) { message.error(error instanceof Error ? error.message : 'Could not load response'); }
    }
    async function resolve(row, outcome) {
        const note = window.prompt(`Reason for marking this usage ${outcome}?`);
        if (!note?.trim()) return;
        try { await api(`/v1/admin/accounts/${account.id}/api-calls/${row.id}/resolve`, { method: 'POST', body: JSON.stringify({ outcome, reason: note }) }); await loaded.reload(); await onChanged(); }
        catch (error) { message.error(error instanceof Error ? error.message : 'Could not reconcile usage'); }
    }
    const data = loaded.data;
    return <>
      <Drawer title={`${account?.name ?? ''} · Kivonex Wallet`} open={Boolean(account)} onClose={onClose} size={900} destroyOnHidden>
        <ErrorNotice error={loaded.error}/>
        {data && <>
          <Space wrap size="large"><strong>Total {money(data.wallet.balancePaise)}</strong><span>Reserved {money(data.wallet.reservedPaise)}</span><Tag color={data.wallet.availablePaise > 0 ? 'green' : 'default'}>Available {money(data.wallet.availablePaise)}</Tag></Space>
          <Panel title="Manual adjustment" description="Ledger rows are immutable. Every adjustment needs a reason.">
            <Space wrap><InputNumber placeholder="500" min={0.01} precision={2} prefix="₹" value={amount} onChange={(value) => setAmount(Number(value || 0))}/><Input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Required reason" style={{ width: 320 }}/><Button type="primary" disabled={!amount || reason.trim().length < 3} onClick={() => adjust('credit')}>Credit</Button><Button danger disabled={!amount || reason.trim().length < 3 || amount * 100 > data.wallet.availablePaise} onClick={() => adjust('debit')}>Debit</Button></Space>
          </Panel>
          <Tabs items={[
            { key: 'ledger', label: 'Transactions', children: <DataTable rowKey="id" dataSource={data.transactions ?? []} pagination={{ pageSize: 10 }} columns={[{ title: 'When', render: (_v,row) => formatDateTime(row.createdAt) }, { title: 'Type', dataIndex: 'type' }, { title: 'Direction', render: (_v,row) => <Tag color={row.direction === 'credit' ? 'green' : 'red'}>{row.direction}</Tag> }, { title: 'Amount', render: (_v,row) => money(row.amountPaise) }, { title: 'Balance after', render: (_v,row) => money(row.balanceAfterPaise) }, { title: 'Reason', dataIndex: 'note' }]}/> },
            { key: 'usage', label: 'Usage', children: <DataTable rowKey="id" dataSource={data.calls ?? []} pagination={{ pageSize: 10 }} onRow={(row) => ({ onClick: () => inspect(row), style: { cursor: 'pointer' } })} columns={[{ title: 'When', render: (_v,row) => formatDateTime(row.createdAt) }, { title: 'Operation', dataIndex: 'operation' }, { title: 'Status', render: (_v,row) => <Tag color={row.status === 'charged' ? 'green' : row.status === 'failed' ? 'red' : 'orange'}>{row.pricePaise === 0 && row.status === 'charged' ? 'free' : row.status}</Tag> }, { title: 'Fee', render: (_v,row) => money(row.pricePaise) }, { title: 'Request ID', dataIndex: 'providerRequestId' }, { title: '', render: (_v,row) => row.status === 'uncertain' && <Space><Button size="small" onClick={(event) => { event.stopPropagation(); void resolve(row, 'success'); }}>Charge</Button><Button size="small" danger onClick={(event) => { event.stopPropagation(); void resolve(row, 'failed'); }}>Release</Button></Space> }]}/> }
          ]}/>
        </>}
      </Drawer>
      <Modal open={Boolean(detail)} title="Stored service response" footer={null} onCancel={() => setDetail(null)} width={780} destroyOnHidden><pre style={{ maxHeight: 520, overflow: 'auto', padding: 14, background: '#0f172a', color: '#e2e8f0' }}>{JSON.stringify(detail?.response ?? null, null, 2)}</pre></Modal>
    </>;
}
const CREDIT_LABEL = { activation: 'Activation', renewal: 'Renewal' };
const REASON_LABEL = { credit: 'Credited', debit: 'Debited', activation: 'Activation given', renewal: 'Renewal given' };
/**
 * Dealer settings of an account: whether it is a dealer, and its Activation and Renewal credits (kept apart). Credits
 * are given here by hand after a deal made outside the system; each activation or renewal the dealer gives debits them.
 */
function DealerDrawer({ account, onClose, onChanged }) {
    const { message } = App.useApp();
    const loaded = useLoad(() => account ? api(`/v1/admin/dealers/${account.id}`) : Promise.resolve(null), [account?.id]);
    const [kind, setKind] = useState('activation');
    const [units, setUnits] = useState(null);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const data = loaded.data;
    async function setDealer(dealer) {
        setBusy(true);
        try {
            await api(`/v1/admin/dealers/${account.id}`, { method: 'PUT', body: JSON.stringify({ dealer }) });
            message.success(dealer ? `${account.name} is a dealer` : `${account.name} is no longer a dealer`);
            await loaded.reload();
            await onChanged();
        }
        catch (error) { message.error(error instanceof Error ? error.message : 'Could not change the dealer setting'); }
        finally { setBusy(false); }
    }
    async function adjust(direction) {
        setBusy(true);
        try {
            const count = Number(units || 0);
            await api(`/v1/admin/dealers/${account.id}/credits`, { method: 'POST', body: JSON.stringify({ kind, units: direction === 'credit' ? count : -count, note }) });
            message.success(`${count} ${CREDIT_LABEL[kind]} ${count === 1 ? 'credit' : 'credits'} ${direction === 'credit' ? 'added' : 'taken away'}`);
            setUnits(null);
            setNote('');
            await loaded.reload();
            await onChanged();
        }
        catch (error) { message.error(error instanceof Error ? error.message : 'Could not change the credits'); }
        finally { setBusy(false); }
    }
    const balance = data?.balances?.[kind] ?? 0;
    return (<Drawer title={`${account?.name ?? ''} · Dealer`} open={Boolean(account)} onClose={onClose} size={860} destroyOnHidden>
      <ErrorNotice error={loaded.error}/>
      {data && <>
        <div className="switch-row">
          <div>
            <strong>Dealer</strong>
            <span>A dealer sees the Dealer pages in the account panel and can activate and renew other accounts with its credits.</span>
          </div>
          <Switch aria-label="Dealer" checked={data.dealer} loading={busy} onChange={setDealer}/>
        </div>
        {data.dealer ? <>
          <Space size="large" wrap style={{ margin: '18px 0' }}>
            <Tag color="green" style={{ padding: '6px 12px', fontSize: 14 }}>Activation credits: <strong>{data.balances.activation}</strong></Tag>
            <Tag color="blue" style={{ padding: '6px 12px', fontSize: 14 }}>Renewal credits: <strong>{data.balances.renewal}</strong></Tag>
          </Space>
          <Panel title="Credit or debit" description="One Activation credit activates one new account for life. One Renewal credit renews an activated account for one year. Every change is kept in the history below.">
            <Space wrap>
              <Segmented value={kind} onChange={setKind} options={[{ value: 'activation', label: 'Activation' }, { value: 'renewal', label: 'Renewal' }]}/>
              <InputNumber placeholder="10" min={1} max={10000} precision={0} value={units} onChange={setUnits} suffix="credits" style={{ width: 150 }}/>
              <Input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Note, e.g. deal or receipt number" maxLength={300} style={{ width: 280 }}/>
              <Button type="primary" disabled={!units || busy} onClick={() => adjust('credit')}>Credit</Button>
              <Button danger disabled={!units || busy || Number(units) > balance} onClick={() => adjust('debit')}>Debit</Button>
            </Space>
          </Panel>
          <DataTable rowKey="id" dataSource={data.entries} pagination={data.entries.length > 10 ? { pageSize: 10 } : false} locale={{ emptyText: 'No credits given yet.' }} columns={[
            { title: 'When', width: 170, render: (_v, row) => formatDateTime(row.createdAt) },
            { title: 'Credits', width: 110, render: (_v, row) => <Tag color={row.kind === 'renewal' ? 'blue' : 'green'}>{CREDIT_LABEL[row.kind]}</Tag> },
            { title: 'What', width: 150, render: (_v, row) => REASON_LABEL[row.reason] ?? row.reason },
            { title: 'Change', width: 90, align: 'right', render: (_v, row) => <span className="num" style={{ color: row.units > 0 ? '#087a3e' : '#b42318' }}>{row.units > 0 ? `+${row.units}` : row.units}</span> },
            { title: 'Left', width: 70, align: 'right', render: (_v, row) => <span className="num">{row.balanceAfter}</span> },
            { title: 'Account / note', render: (_v, row) => row.target ? <span>{row.target.name}<span className="cell-sub">{row.target.mobile}{row.years ? ` · ${row.years} ${row.years === 1 ? 'year' : 'years'}` : ''}</span></span> : (row.note || <span className="cell-sub">—</span>) },
            { title: 'By', width: 140, dataIndex: 'actor' }
          ]}/>
        </> : <Alert type="info" showIcon style={{ marginTop: 18 }} title="Turn on Dealer to give this account Activation and Renewal credits."/>}
      </>}
    </Drawer>);
}
/** What is searched for: a number typed as "+91 98765 43210" is searched as its digits, as the API keeps them. */
function searchTerm(text) {
    const value = text.trim();
    if (!/^[+\d][\d\s-]*$/.test(value))
        return value;
    const digits = value.replace(/\D/g, '');
    return digits.length > 10 && digits.startsWith('91') ? digits.slice(2) : digits;
}
export function AccountsPage() {
    const { message } = App.useApp();
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('all');
    const [open, setOpen] = useState(false);
    const [walletAccount, setWalletAccount] = useState(null);
    const [dealerAccount, setDealerAccount] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    // Search as the admin types, once they pause.
    useEffect(() => {
        const timer = window.setTimeout(() => setQuery(searchTerm(search)), 300);
        return () => window.clearTimeout(timer);
    }, [search]);
    const { data, error, loading, reload } = useLoad(() => api(`/v1/admin/accounts?q=${encodeURIComponent(query)}`).then((body) => body.accounts), [query]);
    const accounts = data ?? [];
    const shown = accounts.filter((row) => (filter === 'all' ? true : filter === 'active' ? !row.disabledAt : filter === 'dealers' ? Boolean(row.dealer?.active) : Boolean(row.disabledAt)));
    const dealerCount = accounts.filter((row) => row.dealer?.active).length;
    const activeCount = accounts.filter((row) => !row.disabledAt).length;
    async function toggle(row) {
        try {
            await api(`/v1/admin/accounts/${row.id}/${row.disabledAt ? 'enable' : 'disable'}`, { method: 'POST' });
            message.success(row.disabledAt ? `${row.name} can sign in again` : `${row.name} is disabled`);
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not change the account');
        }
    }
    async function copy(text, what) {
        try {
            await navigator.clipboard.writeText(text);
            message.success(`${what} copied`);
        }
        catch {
            message.error(`Could not copy the ${what.toLowerCase()}`);
        }
    }
    async function create(values) {
        setSaving(true);
        try {
            await api('/v1/admin/accounts', { method: 'POST', body: JSON.stringify({ name: values.name, tradeName: values.tradeName?.trim() || undefined, mobile: values.mobile, email: values.email?.trim() || undefined }) });
            message.success(`Account created for ${values.name}`);
            setOpen(false);
            form.resetFields();
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not create the account');
        }
        finally {
            setSaving(false);
        }
    }
    return (<>
      <PageHeader title="Accounts" description="Customers sign in with their mobile number and a one-time code, in the account panel and in the desktop app. Most create their own account there." actions={<Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            New account
          </Button>}/>
      <ErrorNotice error={error}/>

      <Panel flush>
        <div className="toolbar">
          <Input allowClear prefix={<SearchOutlined style={{ color: 'var(--muted)' }}/>} placeholder="Search by name, mobile or email" value={search} onChange={(event) => setSearch(event.target.value)} style={{ width: 320, maxWidth: '100%' }}/>
          <Segmented value={filter} onChange={setFilter} options={[
            { value: 'all', label: countLabel('All', accounts.length, data !== null) },
            { value: 'active', label: countLabel('Active', activeCount, data !== null) },
            { value: 'disabled', label: countLabel('Disabled', accounts.length - activeCount, data !== null) },
            { value: 'dealers', label: countLabel('Dealers', dealerCount, data !== null) }
        ]}/>
        </div>
        <DataTable rowKey="id" loading={loading} dataSource={shown} pagination={shown.length > 20 ? { pageSize: 20, showSizeChanger: false } : false} scroll={{ x: 1170 }} locale={{ emptyText: query || filter !== 'all' ? 'No accounts match.' : 'No accounts yet. Create the first one.' }} columns={[
            {
                title: 'Account',
                skeleton: 'person',
                render: (_value, row) => <Person name={row.name} detail={<span className="num">{row.tradeName || mobileLabel(row.mobile) || 'No mobile'}</span>}/>
            },
            { title: 'Email', dataIndex: 'email', width: 220, render: (value) => value || <span className="cell-sub">—</span> },
            {
                title: 'Billing',
                width: 220,
                render: (_value, row) => row.billing?.name || row.billing?.gstin ? (<span>
                    {row.billing.name || row.name}
                    {row.billing.gstin && <span className="cell-sub mono">GSTIN {row.billing.gstin}</span>}
                  </span>) : (<span className="cell-sub">—</span>)
            },
            { title: 'Created', dataIndex: 'createdAt', width: 150, render: (value) => formatDate(value) },
            { title: 'Wallet', width: 140, render: (_value, row) => <Button type="link" onClick={() => setWalletAccount(row)}>{money(row.wallet?.availablePaise)}</Button> },
            {
                title: 'Dealer',
                width: 170,
                render: (_value, row) => row.dealer?.active
                    ? <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => setDealerAccount(row)}><span className="num">A {row.dealer.balances.activation} · R {row.dealer.balances.renewal}</span></Button>
                    : <span className="cell-sub">—</span>
            },
            { title: 'Status', dataIndex: 'disabledAt', width: 130, skeleton: 'tag', render: (value) => <ActiveTag active={!value}/> },
            {
                title: '',
                width: 56,
                align: 'right',
                skeleton: 'actions',
                render: (_value, row) => (<RowActions label={`Actions for ${row.name}`} actions={[
                        Boolean(row.mobile) && { key: 'copy-mobile', icon: <CopyOutlined />, label: 'Copy mobile', onClick: () => copy(mobileLabel(row.mobile), 'Mobile') },
                        { key: 'dealer', icon: <ShopOutlined />, label: row.dealer?.active ? 'Dealer credits' : 'Make dealer', hint: 'Dealer setting and its Activation / Renewal credits', onClick: () => setDealerAccount(row) },
                        Boolean(row.email) && { key: 'copy-email', icon: <CopyOutlined />, label: 'Copy email', onClick: () => copy(row.email ?? '', 'Email') },
                        row.disabledAt
                            ? { key: 'enable', icon: <CheckCircleOutlined />, label: 'Enable account', hint: 'They can sign in again', onClick: () => toggle(row) }
                            : {
                                key: 'disable',
                                icon: <StopOutlined />,
                                label: 'Disable account',
                                danger: true,
                                hint: 'Signs out every computer and blocks sign-in',
                                confirm: {
                                    title: 'Disable this account?',
                                    description: `${row.name} will not be able to sign in, in the account panel or the desktop app, until it is enabled again.`,
                                    okText: 'Disable'
                                },
                                onClick: () => toggle(row)
                            }
                    ]}/>)
            }
        ]}/>
      </Panel>

      <Drawer title="New account" open={open} onClose={() => setOpen(false)} size={440} destroyOnHidden footer={<Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              Create account
            </Button>
          </Space>}>
        <Form form={form} layout="vertical" requiredMark="optional" onFinish={create}>
          <Form.Item label="Full name" name="name" rules={[{ required: true, message: 'Enter the name.' }]}>
            <Input placeholder="Asha Mehta" autoFocus/>
          </Form.Item>
          <Form.Item label="Trade name" name="tradeName" extra="The business name. Shown on invoices until they add billing details.">
            <Input placeholder="Asha Traders"/>
          </Form.Item>
          <Form.Item label="Mobile number" name="mobile" rules={MOBILE_RULES} extra="They sign in with this number and a one-time code sent to it. No password.">
            <Input prefix={<MobileOutlined style={{ color: 'var(--muted)' }}/>} placeholder="98765 43210" inputMode="tel" autoComplete="off" maxLength={16}/>
          </Form.Item>
          <Form.Item label="Email" name="email" rules={[{ type: 'email', message: 'Enter a valid email.' }]} extra="For receipts and your records.">
            <Input placeholder="asha@company.com" autoComplete="off"/>
          </Form.Item>
        </Form>
      </Drawer>
      <WalletDrawer account={walletAccount} onClose={() => setWalletAccount(null)} onChanged={reload}/>
      <DealerDrawer account={dealerAccount} onClose={() => setDealerAccount(null)} onChanged={reload}/>
    </>);
}
